import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { ActiveObject, resolve } from '../../Container';
import { AlertManager } from '../../packages/alerts/AlertManager';
import { EditorModeInfo } from '../EditorModeInfo';
import { EditorOptions } from '../EditorOptions';
import { EditorSession } from '../EditorSession';
import { GridSystem } from '../GridSystem';
import { ParticipantManager } from '../participants/ParticipantManager';
import { ToolManager } from '../tooling/ToolManager';
import { WidgetManager } from '../workspace/widgets/WidgetManager';
import { MeetingCoffeeBreakWidget } from './MeetingCoffeeBreakWidget';
import { MeetingMode } from './MeetingMode';
import { MeetingModeWidget } from './MeetingModeWidget';
import { resolveActiveMeetingAndPhase } from './MeetingUtil';

export class MeetingModeMechanics extends ActiveObject {

    @resolve(AlertManager) private alerts!: AlertManager;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(GridSystem) private grid!: GridSystem;
    @resolve(ParticipantManager) private participants!: ParticipantManager;
    @resolve(EditorOptions) private options!: EditorOptions;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ToolManager) private tools!: ToolManager;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start() {
        const syncDebounced = debounce(() => this.sync(), 0);
        this.burden(
            this.objects.on('Meeting', 'allocated', syncDebounced),
            this.objects.on('Meeting', 'affected', syncDebounced),
            this.objects.on('Meeting', 'released', syncDebounced),
            this.objects.on('Phase', 'allocated', syncDebounced),
            this.objects.on('Phase', 'affected', syncDebounced),
            this.objects.on('Phase', 'released', syncDebounced),
            this.session.observe(EditorModeInfo, syncDebounced),
        );
        this.sync();
    }

    private sync = () => {

        // Phase might be null if we are locked (not active)
        const [meeting, phase] = resolveActiveMeetingAndPhase(this.objects);

        const mode = this.session.get(EditorModeInfo);
        const paused = !!meeting?.breakStart;

        if (mode.id === MeetingMode.id) {
            // Enable the selected tools, unless paused in which case enable nothing
            this.tools.setEnabled((!paused && !!phase) ? phase.tools : []);
            this.tools.setVisible((!paused && !!phase) ? phase.tools : []);
            this.grid.configure({ enabled: false });
        }

        this.realizeWidget(mode.id === MeetingMode.id && !!meeting);
        this.realizePauseAlert(mode.id === MeetingMode.id && !!meeting && paused);
    };

    private realizePauseAlert = (active?: boolean) => {
        const id = 'meeting-paused';
        const showing = this.alerts.find(x => x.id === id);

        if (showing && !active) {
            this.alerts.close(id);
        }
        else if (!showing && active) {
            this.alerts.invoke({
                id,
                type: 'dialog',
                body: MeetingCoffeeBreakWidget,
                delay: 0,
                modal: true,
            });
        }
    };

    private realizeWidget = (active?: boolean) => {
        const { widgets } = this;

        if (active && widgets.has('meetingMode')) return;

        widgets.delete('meetingMode');
        if (!active) return;

        const dock = widgets.createContainer({
            name: 'meetingMode',
            weight: 100,
            position: ['docked', 'bottom', 'inline'],
            style: 'none',
        });

        dock.add(widgets.createInline({
            ui: MeetingModeWidget,
        }));

        widgets.add(dock);
    };
}
